import {GridPage} from "../../shared/GridPage";
import React, {useState} from "react";
import {ColumnConfig, GridConfig} from "../../shared/Grid";
import {SortState} from "../../../../utils/Grid";
import {Link} from "react-router-dom";
import {DateTime} from "luxon";
import {HoldFlag} from "../HoldsFlag";
import {Consumer} from "../../customer/Consumer";
import {HoldEdit} from "../edit/HoldEdit";
import {HoldNew} from "../new/HoldNew";
import {CurrencyFormatter} from "../../../../utils/CurrencyFormatter";
import {reportValToOrderLink} from "../../../reports/ReportView";

interface HoldInfoProps {
	orderId?: number,
	holdFlags?: HoldFlag[];
	isLoading?: boolean;
	onShouldRefresh?: () => void
}

export const HoldInfo = ({orderId, holdFlags, isLoading, onShouldRefresh}: HoldInfoProps) => {
	// States
	const [focusedHoldFlag, setFocusedHoldFlag] = useState<HoldFlag | undefined>(undefined);
	const [editHoldFlagIsOpen, setEditHoldFlagIsOpen] = useState(false);
	const [newHoldFlagIsOpen, setNewHoldFlagIsOpen] = useState(false);
	const [sort, setSort] = useState<SortState<HoldFlag>>({
		column: 'orderId',
		serverColumn: 'o.Id',
		direction: 'asc'
	});

	// Helper functions
	const onRowClick = (holdFlag: HoldFlag) => {
		setFocusedHoldFlag(holdFlag);
		setEditHoldFlagIsOpen(true);
	};

	const onNewHoldFlagClicked = async () => {
		setNewHoldFlagIsOpen(true);
	};

	const closeModal = (shouldRefresh: boolean | undefined = false) => {
		setFocusedHoldFlag(undefined);
		setNewHoldFlagIsOpen(false);
		setEditHoldFlagIsOpen(false);
		if (shouldRefresh && onShouldRefresh) {
			onShouldRefresh();
		}
	};

	// Other variables
	const config: GridConfig<HoldFlag> = {
		configSections: [
			{
				columns: [
					new ColumnConfig({
						field: 'consumer',
						display: 'Consumer',
						isSortable: false,
						isSearchCriteria: false,
						cellDisplay: val => (
							<Link to={`/admin/consumer/${(val as Consumer)?.id}`}>{(val as Consumer)?.email}</Link>),
					}),
					new ColumnConfig({
						field: 'consumerName',
						display: 'Consumer Name',
						serverColumn: 'consumerName',
						isSortable: false,
						isSearchCriteria: false,
					}),
					new ColumnConfig({
						field: 'orderId',
						display: 'Order Id',
						isSortable: false,
						isSearchCriteria: false,
						cellDisplay: val => reportValToOrderLink(val as number),
					}),
					new ColumnConfig({
						field: 'aba',
						display: 'ABA',
						isSortable: false,
						isSearchCriteria: false,
					}),
					new ColumnConfig({
						field: 'accountNumber',
						display: 'Account Number',
						isSortable: false,
						isSearchCriteria: false,
					}),
					...(orderId === undefined ? [new ColumnConfig<HoldFlag>({
						field: 'orderStatus',
						display: 'Order Status',
						isSortable: false,
						isSearchCriteria: false,
					})] : []),
					...(orderId === undefined ? [new ColumnConfig<HoldFlag>({
						field: 'orderTotal',
						display: 'Order Total',
						isSortable: false,
						isSearchCriteria: false,
						cellDisplay: val => val ? `${CurrencyFormatter.format(val as number)}` : "$-.--",
					})] : []),
					new ColumnConfig({
						field: 'holdFlagStatus.name',
						display: 'Status',
						serverColumn: 'HoldStatus',
						isSortable: false,
						isSearchCriteria: false,
					}),
					new ColumnConfig({
						field: 'holdFlagReason.name',
						display: 'Reason',
						serverColumn: 'HoldReason',
						isSortable: false,
						isSearchCriteria: false,
					}),
					new ColumnConfig({
						field: 'timeOfFlag',
						display: 'Time of Flag',
						isSortable: false,
						isSearchCriteria: false,
						cellDisplay: val => val ? DateTime.fromISO(`${val as string}Z`).setZone("local").toLocaleString(DateTime.DATETIME_SHORT) : 'Never modified.',
					}),
					new ColumnConfig({
						field: 'timeOfFlag',
						display: 'Days on Hold',
						isSortable: false,
						isSearchCriteria: false,
						cellDisplay: val => {
							const currentDate = new Date();
							const timeOfFlag = val ? new Date(`${val as string}Z`) : null;
							if (!timeOfFlag)
								return '0 days ago';
							const timeDifference = currentDate.getTime() - timeOfFlag.getTime();
							return `${Math.floor(timeDifference / 86400000)} days ago`;
						},
					}),
					new ColumnConfig({
						field: 'holdDescription',
						display: 'Description',
						isSortable: false,
						isSearchCriteria: false
					}),
				]
			}
		]
	};

	return (
		<>
			<GridPage records={holdFlags ?? []}
			          gridConfig={config}
			          title={'Holds'}
			          sort={sort}
			          shouldShowCriteriaPanel={false}
			          onNewModalButtonClicked={orderId ? onNewHoldFlagClicked : undefined}
			          onSortChanged={setSort}
					  isLoading={isLoading}
			          onRowClick={onRowClick}
			/>

			{focusedHoldFlag &&
                <HoldEdit
                    requestedHoldFlag={focusedHoldFlag}
                    isOpen={editHoldFlagIsOpen}
                    toggleModal={closeModal}/>
			}

			{orderId &&
                <HoldNew
                    orderId={orderId}
                    isOpen={newHoldFlagIsOpen}
                    toggleModal={closeModal}/>
			}

		</>
	)
};
